/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: SipProfilesPage.tsx
 ??? Created: 2025-01-07
 ?? Description:
    SIP Profiles management page with list view and form integration.
    Manages FreeSWITCH SIP profiles with vertical settings storage.

 ?? Notes:
    - Uses DataGrid for list view with pagination
    - Supports CRUD operations with permission control
    - System profiles are protected from deletion
    - Integrates with telephony database

 ?? Last Updated: 2025-01-07 by Rodrigo Cuadra
----------------------------------------------------------------*/

import { useEffect, useCallback, useMemo } from 'react';
import { Settings, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNavigationStore } from '../../store/navigation';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import { useSipProfileList } from './hooks';
import { SipProfile } from './types';

export function SipProfilesPage() {
  const { t, refreshKey, i18n } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { showError } = useNotifications();
  const tenantSlug = user?.tenant?.slug || t('sipProfiles.defaultTenant');

  // Set page title
  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Generate columns with current translations using useMemo
  const availableColumns = useMemo(() => {
    return [
      { key: 'name', label: t('sipProfiles.name'), visible: true, sortable: true },
      { key: 'description', label: t('sipProfiles.description'), visible: true, sortable: false },
      { key: 'settings', label: t('sipProfiles.settingsCount'), visible: true, sortable: false },
      { key: 'updatedAt', label: t('sipProfiles.lastUpdated'), visible: true, sortable: true },
      { key: 'enabled', label: t('common.status'), visible: true, sortable: true },
      { key: 'createdAt', label: t('sipProfiles.created'), visible: true, sortable: true },
      { key: 'actions', label: t('sipProfiles.actions'), visible: true, sortable: false }
    ];
  }, [t, i18n.language, refreshKey]);

  const formatDate = useCallback((value?: string) => {
    if (!value) {
      return '�';
    }

    const date = new Date(value);
    // Guard against invalid date strings
    if (Number.isNaN(date.getTime())) {
      return '�';
    }

    return new Intl.DateTimeFormat(i18n.language).format(date);
  }, [i18n.language]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
  } = useSipProfileList(availableColumns);

  // Handle edit profile - navigate to edit route
  const handleEditProfile = useCallback((profile: SipProfile) => {
    navigate(`/settings/technology/sip/${profile.uuid ?? profile.id}`);
  }, [navigate]);

  // Render actions (only Edit - SIP profiles cannot be deleted)
  const renderActions = useCallback((profile: SipProfile) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip
        content={t('sipProfiles.editProfile')}
        position="top"
      >
        <button
          onClick={() => handleEditProfile(profile)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleEditProfile, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          titleIcon={<Settings className="h-5 w-5 text-primary" />}
          columns={columns.map(col => ({
            ...col,
            render: col.key === 'name' ? (value, item) => (
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-primary" />
                <span className="font-medium">{value}</span>
              </div>
            ) : col.key === 'description' ? (value) => (
              <span className="text-sm text-muted-foreground truncate block max-w-[300px]">
                {value || '�'}
              </span>
            ) : col.key === 'settings' ? (value, item) => (
              <span className="text-sm text-center block">
                {item.settingsCount || 0}
              </span>
            ) : col.key === 'updatedAt' ? (value) => (
              <div className="text-sm text-muted-foreground">
                {formatDate(value)}
              </div>
            ) : col.key === 'enabled' ? (value) => (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                {value ? t('common.active') : t('common.inactive')}
              </span>
            ) : col.key === 'createdAt' ? (value) => (
              <div className="text-sm text-muted-foreground">
                {formatDate(value)}
              </div>
            ) : col.key === 'actions' ? undefined : undefined
          }))}
          onColumnToggle={handleColumnToggle}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onEdit={handleEditProfile}
          title={t('sipProfiles.title')}
          searchPlaceholder={t('sipProfiles.searchPlaceholder')}
          emptyMessage={t('sipProfiles.noProfiles')}
          renderActions={renderActions}
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll')
          }}
        />
      }
      formView={null}
    />
  );
}

export default SipProfilesPage;

